/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useElements, useStripe } from "@stripe/react-stripe-js";
import { PaymentIntentResult } from "@stripe/stripe-js";
import Zahlungstyp, { ZahlungstypSammlung } from "./Zahlungstyp";

function buildReturnUrl(returnUrlBase: string, returnUrlArguments?: { [argument: string]: string }): string {
    const urlQueryString = new URLSearchParams(returnUrlArguments);
    return urlQueryString ? `${returnUrlBase}?${urlQueryString}` : returnUrlBase;
}

export type ConfirmPaymentArgs = {
    paymentMethod: Zahlungstyp,
    fullName: string,
    eMail: string,
    clientSecret: string,
    returnUrlBase: string,
    returnUrlArguments?: { [argument: string]: string }
}

export type ConfirmPayment = (
    args: ConfirmPaymentArgs,
) => Promise<void>;

// Stripe verbietet einige UnicodeZeichen in der Toplevel-Domain die wir in unserer Validierungslogik zulassen.
// Wir passen deshalb die Email, die wir an Stripe schicken an.
// Die verbotenen Zeichen sind in anderen Teilen der E-Mail scheinbar erlaubt, deshalb können wir '.invalid' anhängen um eine für Stripe gültige,
// aber sicher nicht vergebene Adresse zu erzeugen.
export function macheEMailSicherFuerStripe(emailAdresse: string) : string {
    const toplevelDomain = emailAdresse.match(/\.[^.@]+\.?$/)?.at(0);

    if (!toplevelDomain) {
        return "";
    }

    if (toplevelDomain.match(/[^.\d\w]/)) {
        return `${emailAdresse}.invalid`;
    }

    return emailAdresse;
}

export default function useConfirmPayment(): ConfirmPayment {
    const stripe = useStripe();
    const elements = useElements();

    async function confirmPayment(
        {
            paymentMethod,
            clientSecret,
            returnUrlBase,
            returnUrlArguments,
            eMail,
            fullName,
        }: ConfirmPaymentArgs): Promise<void> {
        if (!stripe || !elements) {
            return;
        }

        let returnUrl = buildReturnUrl(returnUrlBase, returnUrlArguments);

        let result: PaymentIntentResult | null = null;
        switch (paymentMethod) {
            case ZahlungstypSammlung.karte:
                const element = elements.getElement("cardNumber");
                if (!element) {
                    throw new Error("cardNumber-Element ist null");
                }

                // Anderen Unternehmen gestatten, Zahlungen direkt anzunehmen
                //     https://stripe.com/docs/connect/enable-payment-acceptance-guide?platform=web#web-submit-payment-to-stripe
                result = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: element,
                    },
                    return_url: returnUrl,
                    setup_future_usage: null,
                    save_payment_method: false,
                });
                break;
            case ZahlungstypSammlung.giropay:
                // Zahlungen per giropay akzeptieren
                //     https://stripe.com/docs/payments/giropay/accept-a-payment?platform=web#zahlungen-per-giropay-best%C3%A4tigen
                result = await stripe.confirmGiropayPayment(clientSecret, {
                    payment_method: {
                        billing_details: {
                            name: fullName,
                        },
                    },
                    return_url: returnUrl,
                    setup_future_usage: null,
                    save_payment_method: false,
                });
                break;
            case ZahlungstypSammlung.sepa:
                const ibanElement = elements.getElement("iban");
                if (!ibanElement) {
                    throw new Error("iban-Element ist null");
                }

                result = await stripe.confirmSepaDebitPayment(clientSecret, {
                    payment_method: {
                        billing_details: {
                            name: fullName,
                            email: macheEMailSicherFuerStripe(eMail),
                        },
                        sepa_debit: ibanElement,
                    },
                    return_url: returnUrl,
                    save_payment_method: false,
                });
                break;
            case ZahlungstypSammlung.aufRechnung:
                // Nichts tun, da wir hier nichts bestätigen müssen
        }

        if (result?.error) {
            window.location.href = returnUrl;
            return;
        }

        // Bei Kartenzahlung wird nicht durch Stripe umgeleitet. Daher übernehmen wir das.
        returnUrl = buildReturnUrl(returnUrlBase, { ...returnUrlArguments, redirect_status: "succeeded" });
        window.location.href = returnUrl;
    }

    return confirmPayment;
}